import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, PhoneCall, MapPin, Truck, ShieldCheck, RotateCcw, Send, Music2 } from 'lucide-react';
import { CONTACT_INFO, categories, CARD_DESCRIPTOR } from '../../data/mock';

// Inline SVG payment-method logos (Visa, Mastercard, Amex, Discover)
const PaymentLogos = () => (
  <div className="flex flex-wrap items-center gap-2">
    <div className="h-7 w-12 bg-white rounded-sm flex items-center justify-center px-1.5" title="Visa">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="Visa">
        <text x="24" y="22" textAnchor="middle" fontSize="14" fontWeight="900" fill="#1A1F71" fontFamily="Arial, sans-serif" fontStyle="italic">VISA</text>
      </svg>
    </div>
    <div className="h-7 w-12 bg-white rounded-sm flex items-center justify-center" title="Mastercard">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="Mastercard">
        <circle cx="19" cy="16" r="9" fill="#EB001B" />
        <circle cx="29" cy="16" r="9" fill="#F79E1B" />
        <path d="M24 9.5 a9 9 0 0 0 0 13" fill="#FF5F00" />
      </svg>
    </div>
    <div className="h-7 w-12 bg-[#2E77BB] rounded-sm flex items-center justify-center px-1" title="American Express">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="American Express">
        <rect x="0" y="0" width="48" height="32" fill="#2E77BB" />
        <text x="24" y="14" textAnchor="middle" fontSize="6.5" fontWeight="900" fill="#FFFFFF" fontFamily="Arial, sans-serif" letterSpacing="0.6">AMERICAN</text>
        <text x="24" y="22" textAnchor="middle" fontSize="6.5" fontWeight="900" fill="#FFFFFF" fontFamily="Arial, sans-serif" letterSpacing="0.6">EXPRESS</text>
        <text x="24" y="30" textAnchor="middle" fontSize="5" fontWeight="800" fill="#FFFFFF" fontFamily="Arial, sans-serif" letterSpacing="1.2">AMEX</text>
      </svg>
    </div>
    <div className="h-7 w-12 bg-white rounded-sm flex items-center justify-center px-1" title="Discover">
      <svg viewBox="0 0 48 32" className="h-5 w-auto" aria-label="Discover">
        <text x="22" y="21" textAnchor="middle" fontSize="8" fontWeight="800" fill="#231F20" fontFamily="Arial, sans-serif">DISC</text>
        <circle cx="40" cy="18" r="4" fill="#F76B1C" />
      </svg>
    </div>
  </div>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const trust = [
    { icon: Truck, title: 'Free USA Shipping', text: 'USPS on every order' },
    { icon: RotateCcw, title: '30-Day Returns', text: 'Simple, no-hassle' },
    { icon: ShieldCheck, title: 'Secure Checkout', text: 'Encrypted payments' },
  ];

  return (
    <footer className="mt-16 bg-dark-950 text-dark-200">
      {/* Trust strip */}
      <div className="bg-dark-900 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {trust.map((t) => (
            <div key={t.title} className="flex items-center gap-3 justify-center sm:justify-start">
              <div className="w-11 h-11 rounded-md bg-athletic-500/10 border border-athletic-500/30 flex items-center justify-center text-athletic-500">
                <t.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-white font-semibold text-sm uppercase tracking-wide">{t.title}</p>
                <p className="text-dark-400 text-xs">{t.text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-12 gap-10">
          {/* Brand + contact + inline newsletter */}
          <div className="col-span-2 md:col-span-4 lg:col-span-5">
            <Link to="/" className="inline-flex items-center gap-3 mb-4">
              <span className="w-10 h-10 rounded-md bg-dark-900 border-2 border-athletic-500 flex items-center justify-center font-display font-bold text-athletic-500">MW</span>
              <span className="font-display text-2xl font-bold text-white tracking-wide uppercase">Motion <span className="text-athletic-500">Works</span></span>
            </Link>
            <p className="text-dark-400 text-sm leading-relaxed mb-5 max-w-sm">
              A garage-built shop for players who care about tone. We hand-pick instruments and music gear worth owning &mdash; strings, drums, woodwinds, studio kit and more &mdash; shipped fast and free across the USA.
            </p>
            <div className="space-y-2 text-sm mb-6">
              <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-start gap-2 text-dark-300 hover:text-white transition-colors">
                <PhoneCall className="w-4 h-4 mt-0.5 text-athletic-500" /> <span>{CONTACT_INFO.phone}</span>
              </a>
              <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-start gap-2 text-dark-300 hover:text-white transition-colors">
                <Mail className="w-4 h-4 mt-0.5 text-athletic-500" /> <span className="break-all">{CONTACT_INFO.email}</span>
              </a>
              <div className="flex items-start gap-2 text-dark-300">
                <MapPin className="w-4 h-4 mt-0.5 text-athletic-500 flex-shrink-0" />
                <span>{CONTACT_INFO.address.street}<br />{CONTACT_INFO.address.city}, {CONTACT_INFO.address.state} {CONTACT_INFO.address.zip}</span>
              </div>
            </div>
            <form className="flex max-w-sm gap-2" onSubmit={(e) => e.preventDefault()}>
              <input
                type="email"
                placeholder="Email for new arrivals"
                className="flex-1 px-4 py-2.5 rounded-md text-white text-sm bg-white/5 border border-white/10 placeholder-dark-400 focus:outline-none focus:border-athletic-500"
              />
              <button type="submit" className="px-4 py-2.5 rounded-md bg-athletic-500 text-white font-semibold text-sm uppercase tracking-wide flex items-center gap-2 hover:bg-athletic-600 transition-colors">
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>

          {/* Instruments */}
          <div className="lg:col-span-3">
            <h4 className="text-white font-bold text-sm uppercase tracking-[0.15em] mb-4 flex items-center gap-2"><Music2 className="w-4 h-4 text-athletic-500" /> Instruments</h4>
            <ul className="space-y-2.5">
              <li><Link to="/shop" className="text-dark-400 hover:text-athletic-500 text-sm transition-colors">Shop All</Link></li>
              {categories.map((cat) => (
                <li key={cat.id}>
                  <Link to={`/shop?category=${cat.slug}`} className="text-dark-400 hover:text-athletic-500 text-sm transition-colors">{cat.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-bold text-sm uppercase tracking-[0.15em] mb-4">Support</h4>
            <ul className="space-y-2.5">
              {[{ name: 'Shipping', path: '/shipping' }, { name: 'Returns', path: '/returns' }, { name: 'FAQ', path: '/faq' }, { name: 'Privacy', path: '/privacy' }, { name: 'Terms', path: '/terms' }].map((link) => (
                <li key={link.path}><Link to={link.path} className="text-dark-400 hover:text-athletic-500 text-sm transition-colors">{link.name}</Link></li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div className="lg:col-span-2">
            <h4 className="text-white font-bold text-sm uppercase tracking-[0.15em] mb-4">Company</h4>
            <ul className="space-y-2.5">
              <li><Link to="/about" className="text-dark-400 hover:text-athletic-500 text-sm transition-colors">About</Link></li>
              <li><Link to="/why-choose-us" className="text-dark-400 hover:text-athletic-500 text-sm transition-colors">Why Us</Link></li>
              <li><Link to="/our-mission" className="text-dark-400 hover:text-athletic-500 text-sm transition-colors">Our Mission</Link></li>
              <li><Link to="/quality-guarantee" className="text-dark-400 hover:text-athletic-500 text-sm transition-colors">Guarantee</Link></li>
              <li><Link to="/contact" className="text-dark-400 hover:text-athletic-500 text-sm transition-colors">Contact</Link></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10 bg-dark-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col items-center gap-4 text-center">
          <div className="flex items-center gap-3">
            <span className="text-dark-500 text-xs font-medium uppercase tracking-wide">We Accept</span>
            <PaymentLogos />
          </div>
          <p className="text-xs text-dark-500">
            Charges appear on your statement as &quot;<span className="font-semibold text-dark-300">{CARD_DESCRIPTOR}</span>&quot;
          </p>
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3 text-sm text-dark-400 pt-2 border-t border-white/5 w-full justify-center">
            <span>&copy; {currentYear} Motion Works Garage</span>
            <span className="hidden sm:block text-white/15">|</span>
            <span>{CONTACT_INFO.corpName}</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
